import React, { useEffect, useRef, useState } from 'react';

const SEEN_KEY = 'empax_intro_seen';
const YELLOW = '#FBAC18';

const words = [
  { t: 'Story.', gold: false },
  { t: 'Space.', gold: false },
  { t: 'Systems.', gold: false },
  { t: 'Engineered.', gold: true },
];

const Intro = () => {
  const alreadySeen = typeof window !== 'undefined' && sessionStorage.getItem(SEEN_KEY) === '1';
  const [visible, setVisible] = useState(!alreadySeen);
  const [enter, setEnter] = useState(false);
  const [phase, setPhase] = useState(0); // 0 = logo, 1 = words, 2 = exit
  const audioRef = useRef(null);
  const videoRef = useRef(null);
  const timers = useRef([]);

  const playChime = () => {
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return;
      const ctx = audioRef.current || new AC();
      audioRef.current = ctx;
      if (ctx.state === 'suspended') ctx.resume();
      const now = ctx.currentTime;
      const notes = [523.25, 659.25, 783.99, 1046.5];
      notes.forEach((f, i) => {
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        o.type = 'sine';
        o.frequency.value = f;
        o.connect(g);
        g.connect(ctx.destination);
        const t = now + i * 0.16;
        g.gain.setValueAtTime(0, t);
        g.gain.linearRampToValueAtTime(0.05, t + 0.04);
        g.gain.exponentialRampToValueAtTime(0.0001, t + 1.4);
        o.start(t);
        o.stop(t + 1.5);
      });
    } catch (e) { /* no-op */ }
  };

  const clearTimers = () => { timers.current.forEach(clearTimeout); timers.current = []; };

  const runIntro = () => {
    clearTimers();
    document.body.style.overflow = 'hidden';
    window.scrollTo(0, 0);
    setVisible(true);
    setEnter(false);
    setPhase(0);
    const v = videoRef.current;
    if (v) { try { v.currentTime = 0; const pr = v.play(); if (pr) pr.catch(() => {}); } catch (e) { /* no-op */ } }
    timers.current = [
      setTimeout(() => setEnter(true), 80),
      setTimeout(() => setPhase(1), 5300),
      setTimeout(() => setPhase(2), 7100),
      setTimeout(() => {
        setVisible(false);
        sessionStorage.setItem(SEEN_KEY, '1');
        document.body.style.overflow = '';
        window.dispatchEvent(new Event('empax:introDone'));
      }, 8350),
    ];
  };

  useEffect(() => {
    if (!alreadySeen) runIntro();
    const resumeAudio = () => audioRef.current && audioRef.current.state === 'suspended' && audioRef.current.resume();
    window.addEventListener('pointerdown', resumeAudio);
    window.addEventListener('empax:replayIntro', runIntro);
    return () => {
      clearTimers();
      window.removeEventListener('pointerdown', resumeAudio);
      window.removeEventListener('empax:replayIntro', runIntro);
      document.body.style.overflow = '';
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const skip = () => {
    clearTimers();
    setPhase(2);
    setTimeout(() => {
      setVisible(false);
      sessionStorage.setItem(SEEN_KEY, '1');
      document.body.style.overflow = '';
      window.dispatchEvent(new Event('empax:introDone'));
    }, 1250);
  };

  if (!visible) return null;

  return (
    <div
      onClick={skip}
      className={`fixed inset-0 z-[100] flex flex-col items-center justify-center cursor-pointer transition-opacity duration-700 ${phase === 2 ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}
      style={{ backgroundColor: '#041031', transitionDelay: phase === 2 ? '0.5s' : '0s' }}
      data-testid="intro-splash"
    >
      {/* soft brand glow */}
      <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
        <div className="intro-glow-anim w-[58vw] h-[58vw] max-w-[720px] max-h-[720px] rounded-full" style={{ background: 'radial-gradient(circle, rgba(251,172,24,0.10), transparent 62%)' }} />
      </div>

      {/* Phase 0: brand logo video - 1920x1080 h264, matte matches splash bg */}
      <div className={`absolute inset-0 flex items-center justify-center transition-opacity duration-700 ${phase === 0 ? 'opacity-100' : 'opacity-0'}`}>
        <video
          ref={videoRef}
          src="/EX_logo_website.mp4"
          autoPlay
          muted
          playsInline
          preload="auto"
          aria-hidden="true"
          className="w-full h-full object-contain"
          style={{
            opacity: enter ? 1 : 0,
            transition: 'opacity 0.9s ease',
          }}
        />
      </div>

      {/* Phase 1: Words (white + yellow on black) */}
      <div className={`absolute inset-x-0 px-6 flex flex-col items-start gap-y-1 md:flex-row md:flex-wrap md:items-baseline md:justify-center md:gap-x-6 md:gap-y-2 transition-opacity duration-500 ${phase >= 1 ? 'opacity-100' : 'opacity-0'}`}>
        {words.map((w, i) => (
          <span
            key={w.t}
            className={`intro-word font-display font-semibold text-[clamp(2.9rem,9vw,5.5rem)] tracking-[-0.02em] ${phase >= 1 ? 'in' : ''} ${w.gold ? 'font-serif-c italic !font-normal' : 'text-[var(--text)]'}`}
            style={{ color: w.gold ? YELLOW : undefined, transitionDelay: phase >= 1 ? `${i * 0.22}s` : '0s' }}
          >
            {w.t}
          </span>
        ))}
      </div>

      <div className="absolute bottom-10 font-mono text-[9px] tracking-[0.3em] uppercase text-[var(--muted-2)]">Tap to skip</div>
    </div>
  );
};

export default Intro;
